import { useEffect } from 'react';
import { c } from '../theme';

export type ToastState = {
  message: string;
  tone?: 'ok' | 'error';
  /** Archiving is reversible, so the toast offers the way back. */
  action?: { label: string; run: () => void };
};

/**
 * A short-lived message over the column.
 *
 * Floated rather than placed in the flow: an inline notice pushes the list down
 * as it appears and back up as it goes, which moves the row under the reader's
 * thumb at the exact moment they are looking at it.
 */
export function Toast({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }) {
  useEffect(() => {
    // A message with something to press gets longer to press it.
    const ms = toast.action ? 6500 : 4000;
    const t = window.setTimeout(onDismiss, ms);
    return () => window.clearTimeout(t);
  }, [toast, onDismiss]);

  const error = toast.tone === 'error';

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 24,
        zIndex: 55,
        display: 'flex',
        justifyContent: 'center',
        padding: '0 24px',
        pointerEvents: 'none',
      }}
    >
      <div
        role={error ? 'alert' : 'status'}
        aria-live={error ? 'assertive' : 'polite'}
        style={{
          width: 390,
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: toast.action ? '14px 14px 14px 20px' : '16px 20px',
          borderRadius: 999,
          background: error ? c.red : c.ink,
          color: c.cream,
          boxShadow: '0 12px 30px -14px rgba(31,26,31,0.6)',
          animation: 'toastUp .28s ease-out',
          pointerEvents: 'auto',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4, minWidth: 0 }}>{toast.message}</span>

        {toast.action ? (
          <button
            onClick={() => {
              toast.action?.run();
              onDismiss();
            }}
            style={{
              flex: 'none',
              fontSize: 10,
              letterSpacing: '0.16em',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: c.cream,
              border: '1px solid rgba(245,238,219,0.35)',
              borderRadius: 999,
              padding: '9px 14px',
              whiteSpace: 'nowrap',
            }}
          >
            {toast.action.label}
          </button>
        ) : (
          <button
            onClick={onDismiss}
            aria-label="Dismiss"
            style={{ flex: 'none', color: 'rgba(245,238,219,0.6)', display: 'flex', padding: 2 }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
